import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { PillToggle } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { useAvailableToday, useTutorials } from '@/hooks';
import { analyticsClient } from '@/lib';
import { settingsService } from '@/services';
import { LiveTodayExplainerDialog } from './LiveTodayExplainerDialog';

export function LiveTodayPill() {
  const { t } = useTranslation('discover');
  const { user } = useCurrentUser();
  const { isLive, setLive } = useAvailableToday();
  const { tutorials } = useTutorials(user);
  const liveLabel = t('header.liveToday');
  const [explainerOpen, setExplainerOpen] = useState(false);
  const explainerShown = useRef(false);

  const handleLiveToggle = (value: string) => {
    const next = value === liveLabel;
    setLive(next);

    if (next && !tutorials?.liveButtonSeenAt && !explainerShown.current) {
      explainerShown.current = true;
      setExplainerOpen(true);
      analyticsClient.capture('live_today_explainer_shown', {
        session_id: analyticsClient.getCurrentSessionId(),
      });
    }
  };

  const handleExplainerOpenChange = (open: boolean) => {
    setExplainerOpen(open);
    if (!open && user) {
      settingsService
        .updateTutorials(user.uid, { liveButtonSeenAt: new Date().toISOString() })
        .catch(() => {
          // Non-critical: worst case the explainer shows again on another device.
        });
    }
  };

  return (
    <>
      <PillToggle
        type="single"
        color={isLive ? 'green' : 'gray'}
        options={[liveLabel]}
        value={isLive ? liveLabel : ''}
        onValueChange={handleLiveToggle}
        dataTestId="live-today"
      />
      <LiveTodayExplainerDialog open={explainerOpen} onOpenChange={handleExplainerOpenChange} />
    </>
  );
}
